import React from 'react'
import SmartLink from '../SmartLink'
import Text from '../Text'
import styles from './styles.module.css'
import { toPath } from '../../utils/urls'
import sendEvent from '../../utils/analytics'
import Loading from '../CtaLoading/Loading'
import { $interactivePrimary, $interactiveSecondary } from '../../styles/colors/semantics'
import { $black, $neutralGray100, $white } from '../../styles/colors/primitives'
import { COLORS } from '../../styles/constants'

export const DEFAULT_HEIGHT = '48px'
export const DEFAULT_WIDTH = 'max-content'
export const DEFAULT_TITLE_TEXT_TYPE = 'regular'
export const DEFAULT_BACKGROUND = $interactivePrimary.light
export const DEFAULT_TITLE_TO_ICON_GAP = '8px'
export const DEFAULT_BORDER_COLOR = $interactiveSecondary.dark
export const DEFAULT_TEXT_COLOR = $white

// Translate the former prop/sx-driven `ctaStyle` interpolations into an inline
// style object (width/height/background/color/box-shadow/border), matching the
// original branch precedence exactly.
const ctaInlineStyle = ({ fullWidth, special, dark, light, transparent, boxShadow, sx }) => {
  const style = {
    width: fullWidth ? '85%' : (sx.width || DEFAULT_WIDTH),
    height: sx.height || DEFAULT_HEIGHT
  }

  if (transparent) {
    style.background = 'transparent'
  } else if (special) {
    style.background = COLORS.ctaSpecialBg
  } else if (dark) {
    style.background = $black
  } else if (light) {
    style.background = $white
  } else {
    style.background = sx.background || DEFAULT_BACKGROUND
  }

  if (special) {
    style.color = $neutralGray100
  } else if (dark) {
    style.color = $white
  } else if (light) {
    style.color = $neutralGray100
  } else {
    style.color = sx.textColor || DEFAULT_TEXT_COLOR
  }

  if (boxShadow) {
    style.boxShadow = boxShadow
  }

  style.border = (dark || transparent || light)
    ? `1px solid ${sx.borderColor || $interactiveSecondary[light ? 'light' : 'dark']}`
    : 'none'

  return style
}

const ctaClassName = (hasMobileIcon) =>
  hasMobileIcon ? `${styles.ctaBase} ${styles.hasMobileIcon}` : styles.ctaBase

function CtaV2({ title, titleIcon, routeOrUrl, hiddenSuffix, onClick, type, event, fullWidth, special, dark, light, transparent, boxShadow, disabled, sx = {
  height: DEFAULT_HEIGHT,
  width: DEFAULT_WIDTH,
  titleTextType: DEFAULT_TITLE_TEXT_TYPE,
  background: DEFAULT_BACKGROUND,
  titleToIconGap: DEFAULT_TITLE_TO_ICON_GAP,
  borderColor: DEFAULT_BORDER_COLOR,
  textColor: DEFAULT_TEXT_COLOR
}, mobileIcon, isLoading }) {
  if (onClick) {
    return renderAsActionButton(title, titleIcon, onClick, event, fullWidth, special, dark, light, transparent, boxShadow, disabled, sx, mobileIcon)
  } else if (type) {
    return renderAsTypeButton(title, titleIcon, type, fullWidth, special, dark, light, transparent, boxShadow, disabled, sx, mobileIcon, isLoading)
  } else {
    return renderAsLink(title, titleIcon, routeOrUrl, event, fullWidth, special, dark, light, transparent, boxShadow, disabled, sx, mobileIcon, hiddenSuffix)
  }
}

const renderAsActionButton = (title, titleIcon, onClick, event, fullWidth, special, dark, light, transparent, boxShadow, disabled, sx, mobileIcon) => {
  return (
    <button type="button"
            className={ctaClassName(!!mobileIcon)}
            style={ctaInlineStyle({ fullWidth, special, dark, light, transparent, boxShadow, sx })}
            onClick={(e) => { onClick(e); sendEvent(event) }}
            disabled={disabled}
    >
      { renderTitle(title, titleIcon, mobileIcon, sx) }
    </button>
  )
}

const renderAsTypeButton = (title, titleIcon, type, fullWidth, special, dark, light, transparent, boxShadow, disabled, sx, mobileIcon, isLoading) => {
  return (
    <button type={type}
            className={ctaClassName(!!mobileIcon)}
            style={ctaInlineStyle({ fullWidth, special, dark, light, transparent, boxShadow, sx })}
            disabled={disabled}
    >
      {isLoading && <Loading dark={!dark} />}
      {!isLoading && renderTitle(title, titleIcon, mobileIcon, sx)}
    </button>
  )
}

const renderAsLink = (title, titleIcon, routeOrUrl, event, fullWidth, special, dark, light, transparent, boxShadow, disabled, sx, mobileIcon, hiddenSuffix) => {
  return (
    <SmartLink
      href={toPath(routeOrUrl)}
      className={ctaClassName(!!mobileIcon)}
      style={ctaInlineStyle({ fullWidth, special, dark, light, transparent, boxShadow, sx })}
      onClick={() => sendEvent(event)}
    >
      { renderTitle(title, titleIcon, mobileIcon, sx, hiddenSuffix) }
    </SmartLink>
  )
}

const renderTitle = (title, titleIcon, mobileIcon, sx, hiddenSuffix) => {
  const hasMobileIcon = !!mobileIcon
  return (
    <>
      <div className={hasMobileIcon ? styles.mobileIcon : `${styles.mobileIcon} ${styles.mobileIconHidden}`}>{mobileIcon}</div>
      <div
        className={hasMobileIcon ? `${styles.desktopText} ${styles.desktopTextHiddenMobile}` : styles.desktopText}
        style={{ gap: sx.titleToIconGap || DEFAULT_TITLE_TO_ICON_GAP }}
      >
        {/* task #154: visually-hidden continuation names the destination so the
            link's innerText/accessible name is descriptive (Lighthouse link-text)
            without altering the visible short label. */}
        <Text type={sx.titleTextType || DEFAULT_TITLE_TEXT_TYPE}>{title}{hiddenSuffix ? <span className="sr-only"> {hiddenSuffix}</span> : null}</Text>
        {!!titleIcon && titleIcon}
      </div>
    </>
  )
}

export default CtaV2
